<template>
    <div class="search-container">
        <div class="search-input-wrapper">
            <input
                ref="searchInput"
                type="text"
                placeholder="Rechercher vélos, utilisateurs, entreprises..."
                @focus="handleInputFocus"
                @input="handleSearch"
                @keydown.escape="closeSearchResults"
                class="search-input"
                autocomplete="off"
            />
            <i class="fas fa-search search-icon"></i>
            <i v-if="isLoading" class="fas fa-spinner fa-spin loading-icon"></i>
        </div>

        <!-- Backdrop overlay -->
        <div
            v-if="searchIsFocused && (searchResults.length > 0 || hasSearched)"
            class="search-backdrop"
            @click="closeSearchResults"
        ></div>

        <!-- Search Results Modal -->
        <transition name="search-modal">
            <div
                v-if="
                    searchIsFocused && (searchResults.length > 0 || hasSearched)
                "
                class="search-results-modal"
                :class="{ 'menu-is-open': isOpen }"
            >
                <!-- Header -->
                <div class="modal-header">
                    <div class="header-content">
                        <h3 class="results-title">
                            <i class="fas fa-search"></i>
                            Résultats de la recherche
                        </h3>
                        <button
                            @click="closeSearchResults"
                            class="close-button"
                            aria-label="Fermer la recherche"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <!-- Filter Dropdown -->
                    <div class="filter-section">
                        <AppSelect
                            v-model="filterType"
                            placeholder="Filtrer les résultats"
                            optionLabel="label"
                            optionValue="value"
                            class="filter-dropdown"
                            :options="filterOptions"
                        />
                    </div>
                </div>

                <!-- Results Content -->
                <div class="modal-content">
                    <div v-if="isLoading" class="loading-state">
                        <i class="fas fa-spinner fa-spin"></i>
                        <p>Recherche en cours...</p>
                    </div>

                    <div v-else-if="hasNoResults" class="no-results">
                        <i class="fas fa-search"></i>
                        <h4>Aucun résultat trouvé</h4>
                        <p>Essayez avec d'autres mots-clés</p>
                    </div>

                    <div v-else-if="!hasSearched" class="search-prompt">
                        <i class="fas fa-keyboard"></i>
                        <h4>Commencez à taper pour rechercher</h4>
                        <p>
                            Recherchez des vélos, utilisateurs, entreprises...
                        </p>
                    </div>

                    <div v-else class="results-grid">
                        <!-- Entretiens -->
                        <div
                            v-for="entretien in filteredEntretiens"
                            :key="`entretien-${entretien.id}`"
                            @click="
                                navigateTo('entretiens-details', entretien.id)
                            "
                            class="result-card entretien-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="
                                        getBikeImage(
                                            entretien.bike_id || 'default',
                                        )
                                    "
                                    @error="handleImageError"
                                    alt="Vélo"
                                    loading="lazy"
                                />
                                <span class="card-type">Entretien</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">
                                    {{ entretien.id }} -
                                    {{ getBikeName(entretien) }}
                                </h4>
                                <p class="card-subtitle">
                                    {{ entretien.company_name }}
                                </p>
                                <p
                                    v-if="
                                        entretien.firstname ||
                                        entretien.lastname
                                    "
                                    class="card-user"
                                >
                                    {{ entretien.firstname }}
                                    {{ entretien.lastname }}
                                </p>
                                <div class="card-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-calendar"></i>
                                        {{ formatDate(entretien.date) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Bikes -->
                        <div
                            v-for="bike in filteredBikes"
                            :key="`bike-${bike.id}`"
                            @click="navigateTo('bikes-stock-details', bike.id)"
                            class="result-card bike-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="getBikeImage(bike.bikes_catalog_id)"
                                    @error="handleImageError"
                                    alt="Vélo"
                                    loading="lazy"
                                />
                                <span class="card-type">Vélo</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">
                                    {{ bike.id }} - {{ bike.brand }}
                                    {{ bike.model }}
                                </h4>
                                <p class="card-subtitle">
                                    {{ bike.company_name }}
                                </p>
                                <p
                                    v-if="bike.firstname || bike.lastname"
                                    class="card-user"
                                >
                                    {{ bike.firstname }} {{ bike.lastname }}
                                </p>
                                <div class="card-meta">
                                    <span
                                        class="meta-item status"
                                        :class="bike.contract_type"
                                    >
                                        {{
                                            getContractTypeLabel(
                                                bike.contract_type,
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Accessories -->
                        <div
                            v-for="accessory in filteredAccessories"
                            :key="`accessory-${accessory.id}`"
                            @click="
                                navigateTo(
                                    'accessories-stock-details',
                                    accessory.id,
                                )
                            "
                            class="result-card accessory-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="
                                        getAccessoryImage(
                                            accessory.accessories_catalog_id,
                                        )
                                    "
                                    @error="handleImageError"
                                    alt="Accessoire"
                                    loading="lazy"
                                />
                                <span class="card-type">Accessoire</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">
                                    {{ accessory.id }} - {{ accessory.brand }}
                                    {{ accessory.model }}
                                </h4>
                                <p class="card-subtitle">
                                    {{ accessory.company_name }}
                                </p>
                                <p
                                    v-if="
                                        accessory.firstname ||
                                        accessory.lastname
                                    "
                                    class="card-user"
                                >
                                    {{ accessory.firstname }}
                                    {{ accessory.lastname }}
                                </p>
                                <div class="card-meta">
                                    <span
                                        class="meta-item status"
                                        :class="accessory.contract_type"
                                    >
                                        {{
                                            getContractTypeLabel(
                                                accessory.contract_type,
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Users -->
                        <div
                            v-for="user in filteredUsers"
                            :key="`user-${user.id}`"
                            @click="navigateTo('users-details', user.id)"
                            class="result-card user-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="getUserImage(user.id)"
                                    @error="handleImageError"
                                    alt="Utilisateur"
                                    loading="lazy"
                                />
                                <span class="card-type">Utilisateur</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">
                                    {{ user.firstname }} {{ user.lastname }}
                                </h4>
                                <p class="card-subtitle">
                                    {{ user.company_name }}
                                </p>
                                <div class="card-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-envelope"></i>
                                        {{ user.email }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Companies -->
                        <div
                            v-for="company in filteredCompanies"
                            :key="`company-${company.id}`"
                            @click="navigateTo('companies-details', company.id)"
                            class="result-card company-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="getCompanyImage(company.id)"
                                    @error="handleImageError"
                                    alt="Entreprise"
                                    loading="lazy"
                                />
                                <span class="card-type">Entreprise</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">{{ company.name }}</h4>
                                <div class="card-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-building"></i>
                                        ID: {{ company.id }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Offers -->
                        <div
                            v-for="offer in filteredOffers"
                            :key="`offer-${offer.id}`"
                            @click="navigateTo('offers-details', offer.id)"
                            class="result-card offer-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="getCompanyImage(offer.company_id)"
                                    @error="handleImageError"
                                    alt="Offre"
                                    loading="lazy"
                                />
                                <span class="card-type">Offre</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">{{ offer.title }}</h4>
                                <p class="card-subtitle">
                                    {{ offer.company_name }}
                                </p>
                                <div class="card-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-calendar"></i>
                                        {{ formatDate(offer.created_at) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Catalog Items -->
                        <div
                            v-for="bike in filteredBikesCatalog"
                            :key="`bike-catalog-${bike.id}`"
                            @click="
                                navigateTo('bikes-catalog-details', bike.id)
                            "
                            class="result-card catalog-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="getBikeImage(bike.id)"
                                    @error="handleImageError"
                                    alt="Vélo catalogue"
                                    loading="lazy"
                                />
                                <span class="card-type">Catalogue Vélo</span>
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">
                                    {{ bike.brand }} {{ bike.model }}
                                </h4>
                                <div class="card-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-tag"></i>
                                        ID: {{ bike.id }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            v-for="accessory in filteredAccessoriesCatalog"
                            :key="`accessory-catalog-${accessory.id}`"
                            @click="
                                navigateTo(
                                    'accessories-catalog-details',
                                    accessory.id,
                                )
                            "
                            class="result-card catalog-card"
                        >
                            <div class="card-image">
                                <img
                                    :src="getAccessoryImage(accessory.id)"
                                    @error="handleImageError"
                                    alt="Accessoire catalogue"
                                    loading="lazy"
                                />
                                <span class="card-type"
                                    >Catalogue Accessoire</span
                                >
                            </div>
                            <div class="card-content">
                                <h4 class="card-title">
                                    {{ accessory.brand }} {{ accessory.model }}
                                </h4>
                                <div class="card-meta">
                                    <span class="meta-item">
                                        <i class="fas fa-tag"></i>
                                        ID: {{ accessory.id }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { useApiStore } from '@/js/stores/apiStore';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const apiStore = useApiStore();

// Props
defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
});

// Reactive data
const searchResults = ref({});
const searchIsFocused = ref(false);
const filterType = ref('all');
const isLoading = ref(false);
const hasSearched = ref(false);
const searchInput = ref(null);

let debounceTimeout;

// Filter options
const filterOptions = [
    { value: 'all', label: 'Tout' },
    { value: 'entretien', label: 'Entretiens' },
    { value: 'bike', label: 'Vélos (stock)' },
    { value: 'accessory', label: 'Accessoires (stock)' },
    { value: 'bike_catalog', label: 'Vélos (catalogue)' },
    { value: 'accessory_catalog', label: 'Accessoires (catalogue)' },
    { value: 'user', label: 'Utilisateurs' },
    { value: 'company', label: 'Entreprises' },
    { value: 'offer', label: 'Offres' },
];

// Computed properties for filtered results
const filteredEntretiens = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'entretien') {
        return searchResults.value.entretiens || [];
    }
    return [];
});

const filteredBikes = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'bike') {
        return searchResults.value.bikes || [];
    }
    return [];
});

const filteredAccessories = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'accessory') {
        return searchResults.value.accessories || [];
    }
    return [];
});

const filteredUsers = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'user') {
        return searchResults.value.users || [];
    }
    return [];
});

const filteredCompanies = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'company') {
        return searchResults.value.companies || [];
    }
    return [];
});

const filteredOffers = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'offer') {
        return searchResults.value.offers || [];
    }
    return [];
});

const filteredBikesCatalog = computed(() => {
    if (filterType.value === 'all' || filterType.value === 'bike_catalog') {
        return searchResults.value.bikes_catalog || [];
    }
    return [];
});

const filteredAccessoriesCatalog = computed(() => {
    if (
        filterType.value === 'all' ||
        filterType.value === 'accessory_catalog'
    ) {
        return searchResults.value.accessories_catalog || [];
    }
    return [];
});

const hasNoResults = computed(() => {
    return (
        hasSearched.value &&
        !isLoading.value &&
        Object.values(searchResults.value).every(
            arr => !arr || arr.length === 0,
        )
    );
});

// Methods
const handleInputFocus = () => {
    searchIsFocused.value = true;
};

const handleSearch = e => {
    const searchValue = e.target.value.trim();

    clearTimeout(debounceTimeout);

    if (searchValue.length < 2) {
        searchResults.value = {};
        hasSearched.value = false;
        isLoading.value = false;
        return;
    }

    isLoading.value = true;

    debounceTimeout = setTimeout(async () => {
        try {
            const results = await apiStore.apiGet(
                '/api/super-search/' + encodeURIComponent(searchValue),
            );
            searchResults.value = results;
            hasSearched.value = true;
        } catch (error) {
            console.error('Search error:', error);
            searchResults.value = {};
        } finally {
            isLoading.value = false;
        }
    }, 300);
};

const closeSearchResults = () => {
    searchResults.value = {};
    searchIsFocused.value = false;
    filterType.value = 'all';
    hasSearched.value = false;
    isLoading.value = false;
    if (searchInput.value) {
        searchInput.value.value = '';
        searchInput.value.blur();
    }
};

const navigateTo = (routeName, id) => {
    router.push({ name: routeName, params: { id } });
    closeSearchResults();
};

// Utility methods
const getBikeImage = catalogId => {
    return `/storage/assets/images/bikes_pictures/${catalogId}/A.jpg`;
};

const getAccessoryImage = catalogId => {
    return `/storage/assets/images/accessories_pictures/${catalogId}/A.jpg`;
};

const getUserImage = userId => {
    return `/storage/assets/images/users/profil-pictures/${userId}.png`;
};

const getCompanyImage = companyId => {
    return `/storage/assets/images/company/${companyId}.png`;
};

const handleImageError = e => {
    e.target.src = '/storage/assets/images/default.png';
};

const getBikeName = entretien => {
    if (entretien.external_bike) {
        return (
            `${entretien.external_brand || ''} ${
                entretien.external_model || ''
            }`.trim() || 'Vélo externe'
        );
    }
    return (
        `${entretien.bike_brand || ''} ${entretien.bike_model || ''}`.trim() ||
        'Vélo'
    );
};

const getContractTypeLabel = type => {
    const labels = {
        stock: 'Stock',
        leasing: 'Leasing',
        selling: 'Vendu',
        external: 'Externe',
    };
    return labels[type] || type;
};

const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
};
</script>

<style lang="scss" scoped>
@use '@/scss/variables' as vars;

.search-container {
    position: relative;
    width: 100%;
    max-width: 500px;
}

.search-input-wrapper {
    position: relative;
    top: 10px;
    display: flex;
    align-items: center;
}

.search-input {
    width: 100%;
    border: none;
    border-radius: 25px;
    padding: 12px 50px 12px 20px;
    font-size: 14px;
    background: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1),
            0 0 0 3px rgba(vars.$greenColor, 0.1);
        transform: translateY(-1px);
    }

    &::placeholder {
        color: #94a3b8;
    }
}

.search-icon {
    position: absolute;
    right: 18px;
    color: vars.$greenColor;
    font-size: 16px;
    top: 10px;
    pointer-events: none;
}

.loading-icon {
    position: absolute;
    right: 45px;
    color: vars.$greenColor;
    font-size: 14px;
}

.search-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;
    backdrop-filter: blur(2px);
}

.search-results-modal {
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    width: 90vw;
    max-width: 1200px;
    max-height: 80vh;
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 10px 30px rgba(0, 0, 0, 0.1);
    z-index: 999;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.menu-is-open {
        left: calc(50% + 150px);
    }

    @media (max-width: 768px) {
        top: 70px;
        width: 95vw;
        max-height: 75vh;

        &.menu-is-open {
            left: 50%;
        }
    }
}

.modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
}

.header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.results-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: vars.$greenColor;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
        font-size: 16px;
    }
}

.close-button {
    background: none;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #e2e8f0;
        color: #ef4444;
    }

    i {
        font-size: 16px;
    }
}

.filter-section {
    .filter-dropdown {
        max-width: 250px;
    }
}

.modal-content {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

.loading-state,
.no-results,
.search-prompt {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    text-align: center;
    color: #64748b;

    i {
        font-size: 48px;
        margin-bottom: 16px;
        color: #cbd5e1;
    }

    h4 {
        margin: 0 0 8px;
        font-size: 18px;
        font-weight: 500;
        color: #374151;
    }

    p {
        margin: 0;
        font-size: 14px;
    }
}

.results-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
    padding: 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        padding: 16px;
        gap: 12px;
    }
}

.result-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1),
            0 4px 12px rgba(0, 0, 0, 0.05);
        border-color: vars.$greenColor;
    }
}

.card-image {
    position: relative;
    height: 120px;
    overflow: hidden;
    background: #f1f5f9;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.2s ease;
    }

    .result-card:hover & img {
        transform: scale(1.05);
    }
}

.card-type {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.card-content {
    padding: 16px;
}

.card-title {
    margin: 0 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.card-subtitle {
    margin: 0 0 4px;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.card-user {
    margin: 0 0 8px;
    font-size: 12px;
    color: #94a3b8;
}

.card-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #64748b;

    i {
        font-size: 10px;
        opacity: 0.7;
    }

    &.status {
        padding: 3px 8px;
        border-radius: 12px;
        background: #f1f5f9;
        font-weight: 500;

        &.leasing {
            background: #dbeafe;
            color: #1e40af;
        }

        &.selling {
            background: #dcfce7;
            color: #166534;
        }

        &.stock {
            background: #fef3c7;
            color: #92400e;
        }
    }
}

// Card type specific styles
.entretien-card .card-type {
    background: linear-gradient(135deg, #3b82f6, #1e40af);
}

.bike-card .card-type {
    background: linear-gradient(135deg, vars.$greenColor, #059669);
}

.accessory-card .card-type {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.user-card .card-type {
    background: linear-gradient(135deg, #f59e0b, #d97706);
}

.company-card .card-type {
    background: linear-gradient(135deg, #ef4444, #dc2626);
}

.offer-card .card-type {
    background: linear-gradient(135deg, #ec4899, #db2777);
}

.catalog-card .card-type {
    background: linear-gradient(135deg, #64748b, #475569);
}

// Transitions
.search-modal-enter-active {
    transition: all 0.3s ease;
}

.search-modal-leave-active {
    transition: all 0.2s ease;
}

.search-modal-enter-from {
    opacity: 0;
    transform: translateX(-50%) scale(0.95) translateY(-20px);
}

.search-modal-leave-to {
    opacity: 0;
    transform: translateX(-50%) scale(0.98) translateY(-10px);
}
</style>
